import { create } from 'zustand'
import { createGlobalIngestKey, deleteGlobalIngestKey, getGlobalIngestKeys, updateGlobalIngestKey } from '../api/ingestKeys'
import type {
  CreateGlobalIngestKeyPayload,
  CreateGlobalIngestKeyResponse,
  DeleteGlobalIngestKeyResponse,
  GlobalIngestKey,
  ListGlobalIngestKeysResponse,
  UpdateGlobalIngestKeyPayload,
  UpdateGlobalIngestKeyResponse,
} from '../types/ingest'

interface IngestKeysState {
  keys: GlobalIngestKey[]
  loading: boolean
  error: string | null
  lastRequestId: string | null
  fetchGlobalKeys: () => Promise<ListGlobalIngestKeysResponse>
  createGlobalKey: (
    payload: CreateGlobalIngestKeyPayload,
  ) => Promise<CreateGlobalIngestKeyResponse>
  updateGlobalKey: (
    id: string,
    payload: UpdateGlobalIngestKeyPayload,
  ) => Promise<UpdateGlobalIngestKeyResponse>
  revokeGlobalKey: (id: string) => Promise<DeleteGlobalIngestKeyResponse>
  resetError: () => void
}

const useIngestKeysStore = create<IngestKeysState>((set) => ({
  keys: [],
  loading: false,
  error: null,
  lastRequestId: null,
  async fetchGlobalKeys() {
    set({ loading: true, error: null });
    try {
      const response = await getGlobalIngestKeys();
      set({
        keys: response.keys ?? [],
        loading: false,
        lastRequestId: response.request_id ?? null,
      });
      return response;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to load ingest keys.';
      const data = (error as { data?: unknown })?.data;
      const requestId =
        data && typeof data === 'object' && 'request_id' in data
          ? String((data as { request_id?: unknown }).request_id ?? '')
          : null;
      const errorMessage = requestId
        ? `Couldn't load ingest keys. Request ID: ${requestId}`
        : message;

      set({ error: errorMessage, loading: false, lastRequestId: requestId ?? null });
      throw error;
    }
  },
  async createGlobalKey(payload) {
    const response = await createGlobalIngestKey(payload);
    const key = response.key;
    set((state) => ({
      keys: [key, ...state.keys.filter((existing) => existing.id !== key.id)],
      lastRequestId: response.request_id ?? state.lastRequestId,
    }));
    return response;
  },
  async updateGlobalKey(id, payload) {
    const response = await updateGlobalIngestKey(id, payload);
    const key = response.key;
    set((state) => ({
      keys: state.keys.map((existing) => (existing.id === key.id ? key : existing)),
      lastRequestId: response.request_id ?? state.lastRequestId,
    }));
    return response;
  },
  async revokeGlobalKey(id) {
    const response = await deleteGlobalIngestKey(id);
    if (response.deleted) {
      set((state) => ({
        keys: state.keys.filter((existing) => existing.id !== id),
        lastRequestId: response.request_id ?? state.lastRequestId,
      }));
    }
    return response;
  },
  resetError() {
    set({ error: null });
  },
}));

export default useIngestKeysStore
